// src/store/ui/uiSelectors.ts
import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "@/store";

/**
 * Selects the entire UI state from the Redux store.
 * @param state - The root Redux state.
 * @returns The UI slice of state.
 */
export const selectUiState = (state: RootState) => state.ui;

/**
 * Selects the current open modal from the UI state.
 * @returns The current modal value or null if none open.
 */
export const selectModal = createSelector(
  selectUiState,
  (ui) => ui.modal
);

/**
 * Determines if any modal is currently open.
 * @returns True if a modal is open, otherwise false.
 */
export const selectIsModalOpen = createSelector(
  selectUiState,
  (ui) => ui.modal !== null
);

/**
 * Determines if the edit user modal is open.
 * @returns True if the "editUser" modal is open, otherwise false.
 */
export const selectIsEditUserModal = createSelector(
  selectUiState,
  (ui) => ui.modal === "editUser"
);

/**
 * Determines if the invite external modal is open.
 * @returns True if the "inviteExternal" modal is open, otherwise false.
 */
export const selectIsInviteExternalModal = createSelector(
  selectUiState,
  (ui) => ui.modal === "inviteExternal"
);

/**
 * Determines if the create six modal is open.
 * @returns True if the "createSix" modal is open, otherwise false.
 */
export const selectIsCreateSixModal = createSelector(
  selectUiState,
  (ui) => ui.modal === "createSix"
);

export const selectPollingModal = createSelector(
  selectUiState,
  (ui) => ui.pollingModal
);

export const selectShowLoader = (state: RootState) => selectUiState(state).showLoader;
export const selectErrorMessage = (state: RootState) => selectUiState(state).errorMessage;
export const selectConfirm = createSelector(selectUiState, (ui) => ui.confirm);
